import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@shared/api";
import { Banner, Empty, Loading } from "@shared/ui";
import {
  PLATFORM_LABELS,
  addDays,
  formatDayKey,
  formatTimeIn,
  type PlanDetail,
  type Post,
} from "@shared/types";
import { Field } from "@shared/ui";
import { PostEditor } from "./PostEditor";

export function PlanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<PlanDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openPostId, setOpenPostId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setDetail(await api.get<PlanDetail>(`/api/admin/plans/${id}`));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load this plan");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Banner kind="error">{error}</Banner>;
  if (!detail) return <Loading label="Loading plan" />;

  const weekStart = new Date(detail.plan.periodStart * 1000).toISOString().slice(0, 10);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const byDay = new Map<string, Post[]>();
  for (const post of detail.posts) {
    byDay.set(post.dayKey, [...(byDay.get(post.dayKey) ?? []), post]);
  }

  const openPost = detail.posts.find((p) => p.id === openPostId) ?? null;

  return (
    <>
      <div className="page-head">
        <div>
          <Link to="/plans" className="muted" style={{ textDecoration: "none" }}>
            ← Plans
          </Link>
          <h1 style={{ marginTop: 6 }}>{detail.plan.title}</h1>
          <p className="page-sub">
            {detail.workspace.name} · {detail.posts.length} post
            {detail.posts.length === 1 ? "" : "s"} · times shown in {detail.workspace.timezone}
          </p>
        </div>
        <span className={`pill ${detail.plan.status === "approved" ? "ok" : ""}`}>
          {detail.plan.status.replace(/_/g, " ")}
        </span>
      </div>

      <SendForReview plan={detail.plan} onSent={load} />
      <ClientResponses detail={detail} />

      {detail.posts.length === 0 ? (
        <Empty title="This plan has no posts">
          Every topic failed to generate. Check the client's AI key under AI keys.
        </Empty>
      ) : (
        <div className="week">
          {days.map((day) => {
            const posts = byDay.get(day) ?? [];
            return (
              <div key={day} className="week-day">
                <div className="week-day-head">{formatDayKey(day)}</div>
                {posts.length === 0 ? (
                  <p className="muted" style={{ fontSize: 12, padding: "6px 0" }}>
                    Nothing
                  </p>
                ) : (
                  posts.map((post) => (
                    <button
                      key={post.id}
                      className="post-chip"
                      onClick={() => setOpenPostId(post.id)}
                    >
                      <span className="post-chip-time">
                        {formatTimeIn(post.scheduledAt, detail.workspace.timezone)}
                      </span>
                      <span className="post-chip-body">
                        {post.variants[0]?.body.slice(0, 70) ?? "Empty"}
                      </span>
                      <span className="post-chip-meta">
                        {post.variants.map((v) => PLATFORM_LABELS[v.platform] ?? v.platform).join(", ")}
                        {post.variants.some((v) => v.warning) ? " · too long" : ""}
                      </span>
                    </button>
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}

      {openPost ? (
        <PostEditor
          post={openPost}
          timezone={detail.workspace.timezone}
          workspaceId={detail.workspace.id}
          onClose={() => setOpenPostId(null)}
          onChanged={load}
        />
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------------ */

function SendForReview({
  plan,
  onSent,
}: {
  plan: PlanDetail["plan"];
  onSent: () => Promise<void> | void;
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ url: string; sentTo: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function send(rotateLink: boolean) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ url: string; sentTo: number; failures: string[] }>(
        `/api/admin/plans/${plan.id}/send`,
        { rotateLink },
      );
      setResult(res);
      if (res.failures.length) {
        setError(`Some emails did not send: ${res.failures.join("; ")}`);
      }
      await onSent();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not send it");
    } finally {
      setBusy(false);
    }
  }

  const alreadySent = Boolean(plan.sentAt);

  return (
    <div className="card">
      <h2>Client review</h2>
      <p className="muted" style={{ marginTop: 4, marginBottom: 12 }}>
        {alreadySent
          ? "The client has the link. Sending again emails them a reminder."
          : "Emails everyone set up to review, with a link they open on their phone."}
      </p>

      {error ? <Banner kind="error">{error}</Banner> : null}
      {result ? (
        <Banner kind="ok">
          Sent to {result.sentTo} {result.sentTo === 1 ? "person" : "people"}.
        </Banner>
      ) : null}

      {result ? (
        <div style={{ marginBottom: 12 }}>
          <Field label="The link they received">
            <input readOnly value={result.url} onFocus={(e) => e.currentTarget.select()} />
          </Field>
          <button
            className="small"
            onClick={() => {
              void navigator.clipboard?.writeText(result.url);
              setCopied(true);
            }}
          >
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>
      ) : null}

      <div className="actions">
        <button className="primary" onClick={() => void send(false)} disabled={busy}>
          {busy ? "Sending…" : alreadySent ? "Send a reminder" : "Send for review"}
        </button>
        {alreadySent ? (
          <button
            onClick={() => void send(true)}
            disabled={busy}
            title="Replaces the old link so it stops working"
          >
            Send a new link
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ClientResponses({ detail }: { detail: PlanDetail }) {
  const approvals = detail.approvals ?? [];
  const openNotes = detail.posts.flatMap((p) =>
    p.feedback.filter((f) => !f.resolvedAt).map((f) => ({ post: p, note: f })),
  );

  if (approvals.length === 0 && openNotes.length === 0) return null;

  return (
    <div className="card">
      <h2>What the client said</h2>

      {openNotes.length > 0 ? (
        <>
          <h3 style={{ marginTop: 12 }}>
            {openNotes.length} note{openNotes.length === 1 ? "" : "s"} still to act on
          </h3>
          <ul className="list">
            {openNotes.map(({ post, note }) => (
              <li key={note.id} className="list-item">
                <div style={{ minWidth: 0 }}>
                  <div>{note.body}</div>
                  <div className="muted">
                    {note.authorLabel} · {formatDayKey(post.dayKey)}
                  </div>
                </div>
                <span className="pill warn">open</span>
              </li>
            ))}
          </ul>
          <p className="muted" style={{ marginTop: 10 }}>
            Open a post and rewrite it. The notes go into the prompt automatically and are marked
            handled afterwards.
          </p>
        </>
      ) : null}

      {approvals.length > 0 ? (
        <>
          <h3 style={{ marginTop: 18 }}>Decisions</h3>
          <ul className="list">
            {approvals.slice(0, 12).map((a, i) => (
              <li key={i} className="list-item">
                <div style={{ minWidth: 0 }}>
                  <div>
                    <strong>{a.actorLabel}</strong> {a.decision.replace(/_/g, " ")}{" "}
                    {a.scope === "plan" ? "the whole week" : a.scope === "day" ? a.scopeKey : "a post"}
                  </div>
                  {a.note ? <div className="muted">{a.note}</div> : null}
                </div>
                <span className={`pill ${a.decision === "approved" ? "ok" : "warn"}`}>
                  {a.decision === "approved" ? "approved" : "needs work"}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
